import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Avatar from 'react-avatar';

import { getConsentData, approveConsent, denyConsent } from '../ConsentActions';
import { windowLocation } from '../../../util/url';
import { withRouter } from '../../../util/withRouter';
import Header from '../../../components/Header/Header';
import Loading from '../../../components/Loading/Loading';
import ImageSize from '../../../components/ImageSize/ImageSize';

import mcpIcon from '../../../assets/img/mcp.svg';
import claudeIcon from '../../../assets/img/claude.svg';
import claudeCodeIcon from '../../../assets/img/claude-code.svg';
import errorIllustration from '../../../assets/img/illustrations/404.png';

import '../style.scss';
import './ConsentPage.scss';

const MCP_URL = process.env.MCP_URL || 'https://mcp.nave.local:8084';

const APP_ICONS = {
  'claude.io': claudeIcon,
  'claude.com': claudeIcon,
};

const SCOPES = [
  { allowed: true, text: 'Read your team dashboards and value streams' },
  { allowed: true, text: 'Read cycle time, throughput and WIP metrics' },
  { allowed: false, text: 'Change or delete anything in Nave or Trello' },
  { allowed: false, text: 'Access data outside of your dashboards' },
];

const renderScopeIcon = (allowed) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d={allowed ? 'M20 6 9 17l-5-5' : 'M18 6 6 18M6 6l12 12'} />
  </svg>
);

export class ConsentPage extends Component {
  constructor(props) {
    super(props);
    this.state = { profileImgFailed: false };
  }

  componentDidMount() {
    const { routeParams, loadData } = this.props;
    loadData(routeParams.id);
  }

  goToMcp = () => {
    const { routeParams } = this.props;
    windowLocation().replace(`${MCP_URL}/oauth/consent/${routeParams.id}/complete`);
  };

  handleApprove = () => {
    const { routeParams, approve } = this.props;
    approve(routeParams.id).then((success) => {
      if (success) this.goToMcp();
    });
  };

  handleDeny = () => {
    const { routeParams, deny } = this.props;
    deny(routeParams.id).then((success) => {
      if (success) this.goToMcp();
    });
  };

  handleProfileImgError = () => {
    this.setState({ profileImgFailed: true });
  };

  renderUserAvatar(size, round) {
    const { user } = this.props;
    const { profileImgFailed } = this.state;

    if (user.profile.profileImg && !profileImgFailed) {
      return <ImageSize src={user.profile.profileImg} alt="" onError={this.handleProfileImgError} />;
    }

    return <Avatar name={user.profile.name} size={size} round={round} maxInitials={2} />;
  }

  render() {
    const {
      user,
      loading,
      error,
      submitting,
      clientId,
      clientName,
      redirectDomain,
    } = this.props;

    if (loading || submitting || !user || !user.email || !user.profile) {
      return (
        <div className="main consent">
          <Header title="" showHome={false} />
          <div className="consent-wrap">
            <Loading />
          </div>
        </div>
      );
    }

    if (error || !clientId || !redirectDomain || !clientName) {
      return (
        <div className="main consent">
          <div className="downtime">
            <Header showHome={false} />

            <div className="downtime_wrap">
              <div className="downtime_content">
                <h1>Something went wrong</h1>
                <p>You can close this page or go back to</p>
                <a className="btn btn-primary" href="/" id="loginBtn">
                  Let&apos;s Try That Again
                </a>
              </div>

              <div className="downtime_icon">
                <img alt="downtime icon" src={errorIllustration} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isLocalhost = redirectDomain.toLowerCase() === 'localhost';
    const appIcon = isLocalhost && clientName === 'Claude Code'
      ? claudeCodeIcon : (APP_ICONS[redirectDomain.toLowerCase()] || mcpIcon);

    return (
      <div className="main consent">
        <Header title="" showHome={false} />
        <div className="consent-wrap">
          <div className="consent-wrap-inner">
            <header>
              <div className="user-data">
                <div className="avatar">
                  {this.renderUserAvatar(36, '7px')}
                </div>
                <div className="user-info">
                  <h3>{user.profile.name}</h3>
                  <span className="link">{user.email}</span>
                </div>
              </div>
            </header>
            <div className="content-scroll">
              <div className="consent-info">
                <div className="connect">
                  <div className="icon">
                    <Link to={`${isLocalhost ? 'http' : 'https'}://${redirectDomain}`} target="_blank" rel="noopener noreferrer">
                      <img src={appIcon} alt="" />
                    </Link>
                  </div>
                  <div className="dots">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="avatar">
                    {this.renderUserAvatar(42, '10px')}
                  </div>
                </div>

                <div className="info">
                  <h1>Connect {clientName} to Nave</h1>
                  <p>
                    <strong>{clientName}</strong>
                    {' '}
                    wants access to the data behind your dashboards.
                  </p>
                </div>
              </div>
              <div className="scopes">
                <h2>What {clientName} will be able to do</h2>
                <ul>
                  {SCOPES.map(({ allowed, text }) => (
                    <li key={text} className={allowed ? undefined : 'no'}>
                      {renderScopeIcon(allowed)}
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="consent-controls">
              <Button variant="outline-secondary" onClick={this.handleDeny}>Deny</Button>
              <Button onClick={this.handleApprove}>Approve access</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ConsentPage.propTypes = {
  user: {
    email: PropTypes.string,
    profile: {
      name: PropTypes.string,
    },
  },
  routeParams: {
    id: PropTypes.string,
  },
  loading: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  error: PropTypes.string,
  clientId: PropTypes.string,
  clientName: PropTypes.string,
  redirectDomain: PropTypes.string,
  loadData: PropTypes.func.isRequired,
  approve: PropTypes.func.isRequired,
  deny: PropTypes.func.isRequired,
};

ConsentPage.defaultProps = {
  error: null,
  clientId: null,
  clientName: null,
  redirectDomain: null,
  routeParams: {},
  user: null,
};

function mapStateToProps(state) {
  return {
    user: state.auth.user,
    loading: state.consent.loading,
    submitting: state.consent.submitting,
    error: state.consent.error,
    clientId: state.consent.clientId,
    clientName: state.consent.clientName,
    redirectDomain: state.consent.redirectDomain,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    loadData: (id) => dispatch(getConsentData(id)),
    approve: (id) => dispatch(approveConsent(id)),
    deny: (id) => dispatch(denyConsent(id)),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ConsentPage));
